// src/mechanism/api-client.js
import axios from 'axios';
import JSONbig from 'json-bigint';
import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { CookieEngine } from './cookie-engine';
import { showToast } from 'vant';

// 1. 引入 router 和 Storage 用于跳转和深度清理
import router from '@/router';
import { Storage } from '@/mechanism/storage';

const jsonBigParser = JSONbig({ storeAsString: true });

const apiClient = axios.create({
    timeout: 8000, //8秒超时
    transformResponse: [
        function (data) {
            try {
                return jsonBigParser.parse(data);
            } catch (err) {
                return data;
            }
        }
    ]
});

// ==========================================
// 精准环境识别
// ==========================================
const isHarmony = window.__IS_HARMONY__ === true || !!window.HarmonyJSBridge;
console.log("!!!!! 当前环境是否为鸿蒙 isHarmony:", isHarmony);

// ==========================================
// 【鸿蒙专属隔离区】：完全不影响 iOS / Android
// ==========================================
if (isHarmony) {
    apiClient.defaults.adapter = async function (config) {
        try {
            console.log(`🚀 [前端->鸿蒙] 拦截到请求，移交 ArkTS: ${config.url}`);

            // 1. 构造鸿蒙桥接请求的 Promise
            const requestPromise = window.HarmonyJSBridge.httpRequest(JSON.stringify({
                url: config.url,
                method: config.method || 'get',
                headers: config.headers,
                data: config.data,
                timeout: config.timeout // 如果原生支持超时，直接透传给原生
            }));

            // 2. 构造前端 JS 层的超时中断 Promise (防原生假死)
            const timeoutPromise = new Promise((_, reject) => {
                if (config.timeout) {
                    setTimeout(() => {
                        const err = new Error(`timeout of ${config.timeout}ms exceeded`);
                        err.code = 'ECONNABORTED'; // 模拟 Axios 的标准超时错误码
                        err.isAxiosError = true;
                        reject(err);
                    }, config.timeout);
                }
            });

            // 3. 赛跑：请求和超时哪个先完成，就以哪个为准
            const responseStr = await Promise.race(
                config.timeout ? [requestPromise, timeoutPromise] : [requestPromise]
            );

            const response = JSON.parse(responseStr);

            console.log(`📥 [鸿蒙->前端] 收到响应: ${config.url}`);

            const axiosResponse = {
                data: response.data,
                status: response.status,
                statusText: response.status === 200 ? 'OK' : 'Error',
                headers: response.headers || {},
                config: config,
                request: {}
            };

            if (response.status >= 200 && response.status < 300) {
                return axiosResponse;
            } else {
                return Promise.reject({ response: axiosResponse });
            }
        } catch (error) {
            console.error(`❌ [鸿蒙请求代理崩溃/超时]`, error);
            return Promise.reject(error);
        }
    };
}

// ==========================================
// 请求拦截器 (Request Interceptor)
// ==========================================
apiClient.interceptors.request.use(async (config) => {
    config.withCredentials = true;

    // 兼容：Capacitor 原生 或 鸿蒙系统，都需要从 CookieEngine 拿 Cookie 拼接到 Headers 里
    if (Capacitor.isNativePlatform() || isHarmony) {
        const matchedCookies = await CookieEngine.getMatchedCookies(config.url);

        if (matchedCookies) {
            const existingCookie = config.headers['Cookie'] || '';
            config.headers['Cookie'] = existingCookie ? `${existingCookie}; ${matchedCookies}` : matchedCookies;

            // ⚠️ iOS / Android 专属逻辑：同步到 Capacitor 原生 Cookie
            // (鸿蒙系统不要进这里，防止报找不到 CapacitorCookies 插件的错)
            if (Capacitor.isNativePlatform() && !isHarmony) {
                try {
                    const targetOrigin = new URL(config.url).origin;
                    const cookiePairs = matchedCookies.split(';').map(p => p.trim());
                    let didWriteNativeCookie = false;

                    for (const pair of cookiePairs) {
                        const firstEqIdx = pair.indexOf('=');
                        if (firstEqIdx !== -1) {
                            const key = pair.substring(0, firstEqIdx);
                            const value = pair.substring(firstEqIdx + 1);

                            await CapacitorCookies.setCookie({
                                url: targetOrigin,
                                key: key,
                                value: value
                            });
                            didWriteNativeCookie = true;
                        }
                    }
                    if (didWriteNativeCookie && Capacitor.getPlatform() === 'android') {
                        await new Promise(resolve => setTimeout(resolve, 30));
                    }
                } catch (error) {
                    console.error('🔗 [App环境] 同步原生 Cookie 失败:', error);
                }
            }
        }
    }
    return config;
});

// ==========================================
// 响应拦截器 (Response Interceptor)
// ==========================================
apiClient.interceptors.response.use(
    // 成功响应处理
    async (response) => {
        // 兼容：Capacitor 原生 或 鸿蒙系统，都需要拦截 Set-Cookie 存入本地引擎
        if (Capacitor.isNativePlatform() || isHarmony) {
            const headers = response.headers || {};
            // 兼容鸿蒙底层回传时的各种大小写组合
            const setCookieHeader = headers['set-cookie'] || headers['Set-Cookie'] || headers['SET-COOKIE'];

            if (setCookieHeader) {
                console.log('📥 [拦截器] 机制捕获到 Set-Cookie:', setCookieHeader);
                await CookieEngine.processSetCookie(response.config.url, headers);

                // 刚登录拿到内部 Cookie 后，立即触发全局预注入
                if (CookieEngine.isInternalService(response.config.url)) {
                    await CookieEngine.syncToAllNativeDomains();
                }
            }
        }
        return response;
    },
    // 失败响应处理 (拦截 超时 / 401 / 500 等)
    async (error) => {
        // 【新增】：优先拦截超时异常 (包括 Axios 自带超时和自定义的 Promise.race 超时)
        if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
            showToast('请求超时，请检查网络连接');
            return Promise.reject(error);
        }

        if (error.response) {
            const status = error.response.status;

            if (status === 401) {
                // showToast('登录已过期，请重新登录');

                await CookieEngine.clearInternalCookies();
                await Storage.remove('access_token');

                const currentPath = router.currentRoute.value.fullPath;
                if (!currentPath.includes('/login')) {
                    router.replace({
                        path: '/login',
                        query: { redirect: currentPath }
                    });
                }
            }
            else if (status === 403) {
                showToast('请求无权限，请稍后重试');
            }
            else if (status >= 500) {
                showToast('服务器异常，请稍后再试');
            }
        } else {
            // 没有 response 且不是 timeout，大概率是直接断网或者 DNS 解析失败
            showToast('网络请求失败，请检查网络连接');
        }

        return Promise.reject(error);
    }
);

export default apiClient;